import { call, put, select } from 'redux-saga/effects'
import { mapProviderConfigData } from './mapProviderConfigData'
import {
  updateContractConfigData,
  updateContractDetailsData,
  updateContractBillingData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { callApi } from '@/shared/tools/apiSagaHelper'
import {
  selectContractDetailsCurrentContractActive,
  selectCharacteristicsCurrentContractActive,
  selectActiveTabType,
  selectBillingCurrentContractActive,
  selectPreferencesClientSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import {
  selectEnergyAuthCommonData,
  selectExtraRoutesAuthData,
  selectTelecomAuthCommonData,
} from '../../../Login/AuthSelectors'
import {
  BUSINESS_VERTICAL_ENERGY,
  BUSINESS_VERTICAL_TELECOM,
  IS_PROPANE,
} from '../../SubscriptionForm/Common/constants'
import {
  calculateOfferAvailability,
  forceIsRealOrMensualityPayment,
  isPotentialBadPayer,
  providerIsAllowedForBadPayer,
} from './OfferTools'
import {
  updateContractConsumptionData,
  addContractErrors,
  removeErrorFromContract,
} from '../../SubscriptionForm/SubscriptionFormSlice'

import {
  selectAddressCurrentContractActive,
  selectPersonalClientSubscriptionData,
} from '../../SubscriptionForm/SubscriptionFormSelectors'
import { loadPropaneVisibility } from '../Propane/PropaneModuleSagas'
import * as Sentry from '@sentry/react'

export const contractActions = {
  OFFER_MODULE_LOAD_PROVIDERS_V2: 'OFFER_MODULE_LOAD_PROVIDERS_V2',
  OFFER_MODULE_LOAD_OFFERS_V2: 'OFFER_MODULE_LOAD_OFFERS_V2',
  OFFER_MODULE_LOAD_OFFER_INFORMATION_V2: 'OFFER_MODULE_LOAD_OFFER_INFORMATION_V2',
  OFFER_MODULE_LOAD_PROVIDERS_OPTIONS_V2: 'OFFER_MODULE_LOAD_PROVIDERS_OPTIONS_V2',
  OFFER_MODULE_LOAD_PROVIDER_INFORMATION_V2: 'OFFER_MODULE_LOAD_PROVIDER_INFORMATION_V2',
  OFFER_MODULE_LOAD_ENERGY_ORIGIN_V2: 'OFFER_MODULE_LOAD_ENERGY_ORIGIN_V2',
  OFFER_MODULE_VALIDATION_CONTRACT_NOT_DUPLICATED: 'OFFER_MODULE_VALIDATION_CONTRACT_NOT_DUPLICATED',
}

export function* validateContractIsNotDuplicated({ params, id }) {
  const { energyIsDuplicatedValidationUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const response = yield call(callApi, {
      endpoint: energyIsDuplicatedValidationUrl + '?' + params + '&contract_id=' + id,
      showErrorSnackbar: false,
      method: 'GET',
    })

    if (response.duplicatedElec === true) {
      yield put(
        addContractErrors({
          id,
          data: {
            energy_type_duplicated_elec: "Un contrat d'électricité de moins de 30 jours existe déjà pour ce PDL.",
          },
        }),
      )
    } else {
      yield put(
        removeErrorFromContract({
          id,
          data: 'energy_type_duplicated_elec',
        }),
      )
    }
    if (response.duplicatedGas === true) {
      yield put(
        addContractErrors({
          id,
          data: { energy_type_duplicated_gas: 'Un contrat de gaz de moins de 30 jours existe déjà pour ce PCE.' },
        }),
      )
    } else {
      yield put(
        removeErrorFromContract({
          id,
          data: 'energy_type_duplicated_gas',
        }),
      )
    }
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error validating duplicate contract'), { extra: { error } })
    }
  }
}

export function* loadProviders({
  params,
  id,
  loadProviderUrl,
  loadOffersUrl,
  offersParams,
  loadOfferConfigurationUrl,
  loadOrganizationConfigUrl = '',
}) {
  const { providerId, energyType = '' } = yield select(selectContractDetailsCurrentContractActive)
  const contractType = yield select(selectActiveTabType)
  const { lastName } = yield select(selectPersonalClientSubscriptionData)
  const { electricityMeter } = yield select(selectAddressCurrentContractActive)

  yield put(
    updateContractConfigData({
      id,
      data: {
        providerLoading: true,
        providers: {},
      },
    }),
  )
  try {
    const response = yield call(callApi, {
      endpoint: loadProviderUrl + '?' + params,
      showErrorSnackbar: false,
      method: 'GET',
    })
    const newProviders = Object.keys(response ?? {})

    yield put(
      updateContractConfigData({
        id,
        data: {
          providerLoading: false,
          providers: response,
        },
      }),
    )
    const providerIdToString = providerId ? providerId.toString() : null
    let newProviderId = newProviders.includes(providerIdToString) ? providerId : null

    if (typeof contractType === 'string' && contractType.toLowerCase() === BUSINESS_VERTICAL_ENERGY) {
      const isBadPayer = isPotentialBadPayer(
        lastName,
        electricityMeter?.oldOccupant ?? '',
        electricityMeter?.pdlEligibilityData?.elec?.technicalData?.status ?? '',
      )
      const providerIsStillAllowed = yield call(providerIsAllowedForBadPayer, response[providerIdToString], isBadPayer)
      if (!providerIsStillAllowed) {
        newProviderId = null
      }
    }

    yield put(
      updateContractDetailsData({
        id,
        data: {
          providerId: newProviderId,
        },
      }),
    )
    const fullOfferParams = offersParams + '&provider_id=' + newProviderId
    yield call(loadOffers, {
      id,
      loadOffersUrl,
      params: fullOfferParams,
      loadOfferConfigurationUrl,
    })
    if (typeof contractType === 'string' && contractType.toLowerCase() === BUSINESS_VERTICAL_TELECOM) {
      yield call(loadProviderOptions, { id })
    }
    if (typeof contractType === 'string' && contractType.toLowerCase() === BUSINESS_VERTICAL_ENERGY) {
      yield call(loadProviderInformation, { id, providerId: newProviderId, loadOrganizationConfigUrl })
      yield call(loadEnergyOrigin, { id, providerId: newProviderId })
      if (energyType === IS_PROPANE && response[newProviderId]) {
        yield call(loadPropaneVisibility, { contractId: id, workflow: response[newProviderId].workflow?.name || '' })
      }
    }
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading providers'), { extra: { error } })
    }
    yield put(
      updateContractConfigData({
        id,
        data: {
          providerLoading: false,
        },
      }),
    )
    yield put(
      updateContractDetailsData({
        id,
        data: {
          providerId: null,
          offerId: null,
        },
      }),
    )
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading providers'), { extra: { error } })
    }
  }
}

export function* loadOffers({ params, loadOffersUrl, id, loadOfferConfigurationUrl }) {
  const { offerId, providerId } = yield select(selectContractDetailsCurrentContractActive)

  yield put(
    updateContractConsumptionData({
      id,
      data: {
        amounts: { total: [], elec: [], gas: [] },
      },
    }),
  )

  if (providerId === null) {
    yield put(
      updateContractDetailsData({
        id,
        data: {
          offerId: null,
        },
      }),
    )
    yield put(
      updateContractConfigData({
        id,
        data: {
          offers: {},
          offerConfiguration: {},
        },
      }),
    )
    return
  }

  yield put(
    updateContractConfigData({
      id,
      data: {
        offerLoading: true,
        offers: {},
      },
    }),
  )

  try {
    const response = yield call(callApi, {
      endpoint: loadOffersUrl + '?' + params,
      showErrorSnackbar: false,
      method: 'GET',
    })
    yield put(
      updateContractConfigData({
        id,
        data: {
          offerLoading: false,
          offers: response.list ?? {},
        },
      }),
    )
    const values = response.list ? Object.values(response.list) : []
    const newOfferId = values.includes(offerId) ? offerId : null
    yield put(
      updateContractDetailsData({
        id,
        data: {
          offerId: newOfferId,
        },
      }),
    )
    yield call(loadOfferInformation, {
      id,
      loadOfferConfigurationUrl,
    })
  } catch (error) {
    yield put(
      updateContractConfigData({
        id,
        data: {
          offerLoading: false,
          offers: {},
          offerConfiguration: {},
        },
      }),
    )
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading offers'), { extra: { error } })
    }
  }
}

export function* loadOfferInformation({ id, loadOfferConfigurationUrl }) {
  const { offerId } = yield select(selectContractDetailsCurrentContractActive)
  const { realPayment: previousRealPayment } = yield select(selectBillingCurrentContractActive)

  const businessVertical = yield select(selectActiveTabType)

  yield put(
    updateContractConfigData({
      id,
      data: {
        offerConfiguration: {},
      },
    }),
  )
  if (!offerId) {
    return
  }
  try {
    const response = yield call(callApi, {
      endpoint: loadOfferConfigurationUrl + '?offer_id=' + offerId,
      showErrorSnackbar: false,
      method: 'GET',
    })

    if (businessVertical.toLowerCase() === BUSINESS_VERTICAL_ENERGY) {
      const { energyType, prestationType, cdfDelayed } = yield select(selectCharacteristicsCurrentContractActive)
      const { electricityMeter } = yield select(selectAddressCurrentContractActive)
      const offersFromPdl = electricityMeter?.pdlEligibilityData?.offers ?? []
      const { offerIsAvailable, offerErrorMessage, availableBillingType } = yield call(
        calculateOfferAvailability,
        offersFromPdl,
        energyType,
        response.offerZohoId,
        response.currentOfferId,
        prestationType,
        cdfDelayed,
      )
      yield put(
        updateContractConfigData({
          id,
          data: {
            offerConfiguration: response.offer,
            offerIsAvailable,
            offerErrorMessage,
            availableBillingType,
          },
        }),
      )
    } else {
      yield put(
        updateContractConfigData({
          id,
          data: {
            offerConfiguration: response.offer,
          },
        }),
      )
    }
    if (response.offer?.attributes) {
      const realPayment = yield call(forceIsRealOrMensualityPayment, response.offer.attributes)
      yield put(updateContractBillingData({ id, data: { realPayment: realPayment ?? previousRealPayment } }))
    }
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading offer information'), { extra: { error } })
    }
  }
}

export function* loadProviderOptions({ id }) {
  const { providerId, providerOptions } = yield select(selectContractDetailsCurrentContractActive)
  const { productType } = yield select(selectCharacteristicsCurrentContractActive)
  const { loadProviderOptionsUrl } = yield select(selectTelecomAuthCommonData)
  const fullUrl = `${loadProviderOptionsUrl}/${providerId}/${productType}`
  yield put(
    updateContractConfigData({
      id,
      data: {
        providerOptions: [],
      },
    }),
  )
  if (!providerId || !productType) return

  try {
    const response = yield call(callApi, {
      endpoint: fullUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })
    yield put(
      updateContractConfigData({
        id,
        data: {
          providerOptions: response ?? [],
        },
      }),
    )
    const optionsToKeep = []
    if (providerOptions.length > 0) {
      providerOptions.forEach(option => {
        response.forEach(newOption => {
          if (option.id === newOption.id) {
            optionsToKeep.push(option)
          }
        })
      })
    }
    yield put(
      updateContractDetailsData({
        id,
        data: {
          providerOptions: optionsToKeep,
        },
      }),
    )
  } catch (error) {
    yield put(
      updateContractDetailsData({
        id,
        data: {
          providerOptions: [],
        },
      }),
    )
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading provider options'), { extra: { error } })
    }
  }
}

export function* loadProviderInformation({ providerId, id, loadOrganizationConfigUrl }) {
  yield put(
    updateContractConfigData({
      id,
      data: {
        providerConfiguration: {},
      },
    }),
  )
  if (!providerId) {
    return
  }

  try {
    const response = yield call(callApi, {
      endpoint: loadOrganizationConfigUrl + '?provider_id=' + providerId,
      showErrorSnackbar: false,
      method: 'GET',
    })
    yield put(
      updateContractConfigData({
        id,
        data: {
          providerConfiguration: mapProviderConfigData(response),
        },
      }),
    )
    const { disclaimerCommitmentPro: previousValue } = yield select(selectContractDetailsCurrentContractActive)
    const { prestationType } = yield select(selectCharacteristicsCurrentContractActive)
    const { clientType } = yield select(selectPreferencesClientSubscriptionData)
    const canReuseValue =
      response?.allow_disclaimer_commitment_pro === 1 && clientType === 'pro' && prestationType === 'cdf'
    yield put(
      updateContractDetailsData({
        id,
        data: {
          disclaimerCommitmentPro: canReuseValue ? previousValue : null,
        },
      }),
    )
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading organization config'), { extra: { error } })
    }
    yield put(
      updateContractDetailsData({
        id,
        data: {
          disclaimerCommitmentPro: null,
        },
      }),
    )
  }
}

export function* loadEnergyOrigin({ id, providerId }) {
  const { loadEnergyOriginUrl } = yield select(selectEnergyAuthCommonData)

  yield put(
    updateContractConfigData({
      id,
      data: {
        energyOrigin: {},
      },
    }),
  )

  if (!providerId) {
    return
  }
  try {
    const response = yield call(callApi, {
      endpoint: loadEnergyOriginUrl + '?id=' + providerId,
      showErrorSnackbar: false,
      method: 'GET',
    })

    yield put(
      updateContractConfigData({
        id,
        data: {
          energyOrigin: response,
        },
      }),
    )
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('Error loading energy origin'), { extra: { error } })
    }
  }
}
